/**
 * Platform-agnostic UA parsing. Pure + dependency-free so it's trivially
 * unit-testable.
 *
 * Written for the mobile-handoff diagnostics snapshot (LF-5217), then lifted
 * here (VALE-212) as the ONE implementation both AI Scribe UA consumers share:
 *   - `components/ai-scribe/qr-handoff/mobile/diagnostics/use-diagnostics-capture.ts`
 *     — the Rollbar diagnostics device block;
 *   - `components/ai-scribe/recording-flow/client-telemetry.ts` — the write-once
 *     `clientTelemetry` record stored on the recording row.
 * The latter used to carry its own iOS-only `parseIosVersion`, so every Android
 * recording's telemetry landed with a blank OS. Don't reintroduce a second
 * parser: the two capture paths describing the same device differently is the
 * exact drift this move exists to prevent.
 *
 * Capture runs on BOTH iOS and Android, so an Android reading is exactly as
 * important as an iOS one.
 *
 * UA-string parsing (rather than `navigator.userAgentData`) is deliberate: the
 * modern API is unsupported on iOS Safari and only exposes version data
 * asynchronously via `getHighEntropyValues`, neither of which suits a one-shot
 * synchronous capture.
 */

export interface ParsedPlatform {
  /** OS family, e.g. `iOS` / `iPadOS` / `Android` / `Windows` / `macOS`. */
  os?: string;
  /** OS version string, e.g. `18.3.1` (iOS) or `14` (Android). */
  osVersion?: string;
  /** Browser family + major version, e.g. `Safari 18`, `Chrome 126`. */
  browser?: string;
}

export function parsePlatform(userAgent: string): ParsedPlatform {
  let os: string | undefined;
  let osVersion: string | undefined;

  // iOS / iPadOS — "iPhone OS 18_3_1", or iPad's "CPU OS 17_5 like Mac OS X".
  const apple = userAgent.match(/(?:iPhone OS|CPU OS) (\d+)_(\d+)(?:_(\d+))?/);
  const android = userAgent.match(/Android (\d+(?:\.\d+)*)/);

  if (apple && /iPhone|iPad|iPod/.test(userAgent)) {
    os = /iPad/.test(userAgent) ? 'iPadOS' : 'iOS';
    osVersion = [apple[1], apple[2], apple[3]].filter(Boolean).join('.');
  } else if (android) {
    os = 'Android';
    osVersion = android[1];
  } else if (/Windows NT/.test(userAgent)) {
    os = 'Windows';
  } else if (/Mac OS X/.test(userAgent)) {
    os = 'macOS';
  } else if (/Linux/.test(userAgent)) {
    os = 'Linux';
  }

  return { os, osVersion, browser: parseBrowser(userAgent) };
}

/**
 * Browser family + major version. Order matters twice over:
 * - Chromium-DERIVED browsers (Samsung Internet, Edge) also carry a `Chrome`
 *   token, so they must be matched BEFORE the generic Chrome pattern or they'd
 *   be mislabelled "Chrome".
 * - iOS Chrome/Firefox/Edge and Android's branded browsers all carry a `Safari`
 *   token, so Safari is the last-resort fallback.
 */
export function parseBrowser(userAgent: string): string | undefined {
  const patterns: Array<[RegExp, string]> = [
    [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
    [/(?:EdgiOS|EdgA|Edg)\/(\d+)/, 'Edge'],
    [/(?:FxiOS|Firefox)\/(\d+)/, 'Firefox'],
    [/(?:CriOS|Chrome)\/(\d+)/, 'Chrome'],
    [/Version\/(\d+)(?:\.\d+)*\s+(?:Mobile\/\S+\s+)?Safari/, 'Safari']
  ];
  for (const [re, name] of patterns) {
    const m = userAgent.match(re);
    if (m) return `${name} ${m[1]}`;
  }
  return undefined;
}
